import type { Platform, PrinterConfig } from "./types";
import { requestRefocus } from "@/app/refocus";

const ls = {
  getItem: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  setItem: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* modo privado */
    }
  },
  removeItem: (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* noop */
    }
  },
};

let updateSW: ((reload?: boolean) => Promise<void>) | null = null;
let waiting = false;

/** Registra el service worker en modo "prompt": la app decide cuándo aplicar la actualización. */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
  const { registerSW } = await import("virtual:pwa-register");
  updateSW = registerSW({
    onNeedRefresh() {
      waiting = true;
      window.dispatchEvent(new CustomEvent("sofia:update-ready"));
    },
    onRegisteredSW(_url: string, reg: ServiceWorkerRegistration | undefined) {
      // Revisa cada 2 minutos y al volver a la pestaña (regla del sistema original).
      if (!reg) return;
      setInterval(() => reg.update(), 2 * 60_000);
      document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && reg.update());
    },
  });
}

export const webPlatform: Platform = {
  kind: "web",
  async fingerprint() {
    let id = ls.getItem("sofia.fingerprint");
    if (!id) {
      id = `web-${crypto.randomUUID()}`;
      ls.setItem("sofia.fingerprint", id);
    }
    return id;
  },
  async deviceName() {
    return `Navegador ${navigator.userAgent.includes("Mobile") ? "móvil" : "de escritorio"}`;
  },
  authStorage: ls,
  store: ls,
  printers: {
    // Con Sofía Impresión corriendo en la PC, la web imprime directo en USB, red o Bluetooth.
    supported: ["browser", "usb", "network", "bluetooth"],
    async discover(type) {
      if (type === "browser") return [{ connection: { type: "browser" }, label: "Impresora del navegador" }];
      if (type === "network") return [];
      const list = await agentPrinters();
      return type === "usb"
        ? list.usb.map((n) => ({ connection: { type: "usb" as const, printerName: n }, label: n }))
        : list.serial.map((p) => ({ connection: { type: "bluetooth" as const, address: p }, label: `${p} (Bluetooth o serie)` }));
    },
    async send(config, bytes, html) {
      if (config.connection.type !== "browser") {
        try {
          await agentPrint(config, bytes);
          return;
        } catch (e) {
          // Sin Sofía Impresión se cae al diálogo del navegador para no dejar la venta sin ticket.
          if (!(e instanceof AgentUnavailable) || !html) throw e;
        }
      }
      if (!html) throw new Error("En la web se imprime con el diálogo del navegador");
      browserPrint(html);
    },
  },
  updater: {
    async check() {
      if (!waiting) return null;
      // La versión y las novedades salen de version.json (lo arma el build; no lo guarda el service worker).
      const v = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)).catch(() => null) as { version?: string; notes?: string } | null;
      return { version: v?.version ?? "nueva", notes: v?.notes, mandatory: false };
    },
    async download(h) {
      // El navegador ya bajó los archivos nuevos en segundo plano (service worker en espera).
      h.log("info", "Los archivos de la versión nueva se bajaron en segundo plano");
      h.progress(1, 1);
      h.log("ok", "Descarga completa");
    },
    async apply(h) {
      h?.log("info", "Activando la versión nueva (se reemplazan los archivos guardados en el navegador)…");
      h?.log("info", "La página se recarga sola; los datos del equipo y los borradores se conservan");
      await updateSW?.(true);
    },
  },
  async share(opts) {
    if (navigator.share) {
      const list = [...(opts.file ? [opts.file] : []), ...(opts.files ?? [])];
      const files = list.length ? list.map((f) => new File([f.data], f.name, { type: f.data.type })) : undefined;
      await navigator.share({ title: opts.title, text: opts.text, url: opts.url, files });
    } else if (opts.url) {
      window.open(opts.url, "_blank", "noopener");
    }
  },
  async openExternal(url) {
    window.open(url, "_blank", "noopener");
  },
  onOnlineChange(cb) {
    const on = () => cb(true);
    const off = () => cb(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  },
  async isOnline() {
    return navigator.onLine;
  },
};

// ---------------------------------------------------------------------------
// Sofía Impresión (servidor local en 127.0.0.1:17891)
// ---------------------------------------------------------------------------
export const AGENT_URL = "http://127.0.0.1:17891";
export class AgentUnavailable extends Error {}

/** ¿Está corriendo Sofía Impresión en esta PC? (no pide autorización). */
export async function agentStatus(): Promise<{ ok: boolean; version?: string }> {
  try {
    const r = await fetch(`${AGENT_URL}/status`, { signal: AbortSignal.timeout(1500) });
    const j = (await r.json()) as { ok: boolean; version?: string };
    return { ok: !!j.ok, version: j.version };
  } catch {
    return { ok: false };
  }
}

async function agentFetch(path: string, init?: RequestInit): Promise<Response> {
  try {
    // La primera vez, Sofía Impresión muestra en la PC "¿Permitir que … imprima?": se le da tiempo.
    return await fetch(`${AGENT_URL}${path}`, { ...init, signal: AbortSignal.timeout(60000) });
  } catch (e) {
    // Se venció el tiempo: el programa está (quizás esperando la autorización). No se imprime por el
    // navegador, porque Sofía Impresión podría imprimir igual después y saldría dos veces.
    if ((e as Error)?.name === "TimeoutError" || (e as Error)?.name === "AbortError") {
      throw new Error("Sofía Impresión no respondió a tiempo. Si en la PC hay una ventana preguntando si autorizás, respondela y reimprimí.");
    }
    throw new AgentUnavailable("Sofía Impresión no está abierto en esta PC. Instalalo desde Impresoras o abrilo desde el menú Inicio.");
  }
}

async function agentPrinters(): Promise<{ usb: string[]; serial: string[] }> {
  const r = await agentFetch("/printers");
  const j = (await r.json()) as { usb?: string[]; serial?: string[]; error?: string };
  if (!r.ok) throw new Error(j.error ?? "Sofía Impresión no respondió");
  return { usb: j.usb ?? [], serial: j.serial ?? [] };
}

async function agentPrint(config: PrinterConfig, bytes: Uint8Array) {
  const c = config.connection;
  const q = new URLSearchParams({ copies: String(Math.max(1, config.copies || 1)) });
  if (c.type === "usb") { q.set("type", "usb"); q.set("name", c.printerName); }
  else if (c.type === "network") { q.set("type", "network"); q.set("host", c.host); q.set("port", String(c.port || 9100)); }
  else if (c.type === "bluetooth") { q.set("type", "serial"); q.set("port", c.address); }
  const r = await agentFetch(`/print?${q}`, { method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: new Blob([bytes as BlobPart]) });
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? `La impresora no respondió (${r.status})`);
  }
}

function browserPrint(html: string) {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;";
  document.body.appendChild(frame);
  frame.contentDocument!.write(`<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0">${html}</body></html>`);
  frame.contentDocument!.close();
  frame.contentWindow!.focus();
  frame.contentWindow!.print();
  // El marco de impresión se lleva el foco: al terminar se saca y la pantalla (el punto de venta) lo recupera.
  setTimeout(() => { frame.remove(); requestRefocus(); }, 300);
}
